/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../../styles/ThemeContext';
import { TypeDhlFooter } from './TypeDhlFooter';
import { device } from '../../styles/Device';
import DhlText from '../DhlText';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DhlImage } from '@dhl-official/react-library';
import DHLLogo from '../../assets/images/DHLGroupLogo_black.svg';
import moment from 'moment';

interface VersionData {
  version: string;
  build: string;
  commit: string;
  releaseDate: string;
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 1920px;
  height: 140px;
  display: flex;
  flex-direction: row;
  padding: ${props => props.theme.spacing.lg};
  @media ${device.mobile} {
    flex-wrap: wrap;
  }
  @media ${device.tablet} {
    flex-wrap: nowrap;
  }
`;

const MetaWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  @media ${device.mobile} {
    justify-content: center;
  }
  @media ${device.tablet} {
    justify-content: flex-start;
  }
  margin-top: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const MetabButton = styled.div`
  display: flex;
  flex-direction: row;
  text-decoration: underline #333333;
  margin-right: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.xs};
  cursor: pointer;
  font-size: 12px;
  color: #333333;
`;

const LeftWrapper = styled.div`
  @media ${device.mobile} {
    width: 100%;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-left: ${props => props.theme.spacing.xs};
  }
  @media ${device.tablet} {
    width: 50%;
    align-items: flex-start;
    justify-content: center;
    margin-left: ${props => props.theme.spacing.xxl};
  }
  display: flex;
  flex-direction: column;
`;

const RightWrapper = styled.div`
  @media ${device.mobile} {
    width: 100%;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-right: ${props => props.theme.spacing.xs};
  }
  @media ${device.tablet} {
    width: 50%;
    align-items: flex-end;
    justify-content: flex-end;
    margin-right: ${props => props.theme.spacing.xxl};
  }
  display: flex;
  flex-direction: column;
`;

const DhlLogo = styled(DhlImage)`
  position: relative;
  width: 169px;
`;

const DhlFooter: React.FC<TypeDhlFooter> = props => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { metaData, copyright, versionData } = props;
  const [version, setVersion] = useState<VersionData | null>(null);

  const openCookieSettings = (): void => {
    type OneTrustWindow = Window & {
      OneTrust?: { ToggleInfoDisplay?: () => void };
      Optanon?: { ToggleInfoDisplay?: () => void };
      OptanonToggleInfoDisplay?: () => void;
    };

    const w: OneTrustWindow = window;

    if (typeof w.OneTrust?.ToggleInfoDisplay === 'function') {
      w.OneTrust.ToggleInfoDisplay();
      return;
    }
    if (typeof w.Optanon?.ToggleInfoDisplay === 'function') {
      w.Optanon.ToggleInfoDisplay();
      return;
    }
    if (typeof w.OptanonToggleInfoDisplay === 'function') {
      w.OptanonToggleInfoDisplay();
      return;
    }

    // Fallback: OneTrust often wires click handlers to this class.
    const el = document.querySelector<HTMLElement>('.ot-sdk-show-settings');
    el?.click();
  };

  useEffect(() => {
    const loadVersion = async (): Promise<void> => {
      try {
        // eslint-disable-next-line import/no-unresolved
        const module = await import('../../assets/version.json');
        const data: VersionData = module.default;
        setVersion(data);
      } catch (err) {
        console.warn('version.json not found — skipping version info.');
      }
    };

    void loadVersion();
  }, []);

  const renderVersion = (): string => (version?.version != null ? version.version : versionData.version);

  const renderReleaseDate = (): string => {
    const releaseDate = version?.releaseDate != null ? version.releaseDate : versionData.releaseDate;
    return moment(releaseDate).format('YYYY-MM-DD HH:mm');
  };

  const renderYear = (): string =>
    version?.releaseDate != null ? moment(version.releaseDate).year().toString() : moment().year().toString();

  return (
    <Wrapper theme={theme}>
      <LeftWrapper theme={theme}>
        <DhlLogo src={DHLLogo} alt="DHL Group" />

        <MetaWrapper theme={theme}>
          {metaData?.map(metaDetail =>
            metaDetail.label === 'App.footer.CookieSettings' ? (
              <MetabButton
                data-tracking={metaDetail.dataTracking}
                theme={theme}
                key={metaDetail.label}
                onClick={openCookieSettings}
              >
                <DhlText size="sm" color={'#333333'} weight={200} title={t(metaDetail.label)} />
              </MetabButton>
            ) : (
              <MetabButton
                data-tracking={metaDetail.dataTracking}
                theme={theme}
                key={metaDetail.label}
                onClick={() => {
                  if (metaDetail.external) window.open(metaDetail.href, '_blank');
                  else navigate(metaDetail.href);
                }}
              >
                <DhlText size="sm" color={'#333333'} weight={200} title={t(metaDetail.label)} />
              </MetabButton>
            )
          )}
        </MetaWrapper>

        <DhlText
          size="sm"
          color={'#333333'}
          weight={200}
          title={`${t('App.footer.applicationVersion')} ${renderVersion()} - ${t('App.footer.releaseDate')} ${renderReleaseDate()}`}
        />
      </LeftWrapper>

      <RightWrapper theme={theme}>
        <DhlText size="sm" color={'#333333'} weight={200} title={t(copyright, {year: renderYear()})} />
      </RightWrapper>
    </Wrapper>
  );
};

export default DhlFooter;
